import { EditPencil } from 'iconoir-react'
import { useEffect, useState } from 'react'
import ImageCropModalContent from '../shared/image-crop/ImageCropModal'
import { useImageCropContext } from '../shared/image-crop/ImageCropProvider'
import NameIcon from '../shared/NameIcon'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { lastNameInitial } from 'lib/nameUtils'

const getNameInitials = (name: string) => {
  if (!name) return `?`
  return `${name[0]}${lastNameInitial(name)?.toLocaleUpperCase() ?? ``}`
}

export function ProviderAvatar({
  name,
  providerId,
  remoteId,
  pmsDisplayName,
  hasPicture,
  priority,
  onSaveProvider,
  isSecondary,
}: {
  name: string
  providerId: string
  remoteId: string
  pmsDisplayName?: string | null
  hasPicture?: boolean
  priority?: number
  onSaveProvider: (name: string, priority?: number) => Promise<void>
  isSecondary?: boolean
}) {
  const [preview, setPreview] = useState<string>()
  const { getProcessedImage, resetStates } = useImageCropContext()
  const [isDone, setIsDone] = useState(false)
  const [savedName, setSavedName] = useState(name)
  const [savedPriority, setSavedPriority] = useState(priority)
  const onDone = (name: string, newPriority?: number) => {
    setSavedName(name)
    setSavedPriority(newPriority)
    setIsDone(true)
  }

  const handleDone = async () => {
    if (savedName !== name || savedPriority !== priority) {
      await onSaveProvider(savedName, savedPriority)
    }
    const profilePicture = await getProcessedImage()
    if (!profilePicture) {
      resetStates()
      closeModal()
      notifySuccess(`Provider updated`)
      return
    }
    setPreview(window.URL.createObjectURL(profilePicture))
    const formData = new FormData()
    formData.append(`profilePicture`, profilePicture)
    window
      .fetch(`/api/providers/${providerId}/avatar`, {
        method: `POST`,
        credentials: `include`,
        body: formData,
      })
      .then((r) => r.json())
      .then((data) => {
        if (!data?.fileUrl) {
          notifyError(`Error updating profile picture`)
        }
      })
    resetStates()
    closeModal()
    notifySuccess(`Provider updated`)
  }

  const handleDelete = async () => {
    const res = await window.fetch(`/api/providers/${providerId}/avatar`, {
      method: `DELETE`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({}),
    })
    const resp = await res.json()
    if (resp) {
      setPreview(undefined)
      closeModal()
      notifySuccess(`Profile picture deleted`)
    } else {
      notifyError(`Error deleting profile picture`)
    }
  }

  const handleCancel = () => {
    resetStates()
    closeModal()
  }

  const onAvatarClick = (e) => {
    e.preventDefault()
    openProfilePictureModal()
  }

  const openProfilePictureModal = () => {
    showModal(
      <ImageCropModalContent
        currentImage={preview}
        handleDone={onDone}
        handleCancel={handleCancel}
        handleDelete={handleDelete}
        shape="rect"
        name={name}
        priority={priority}
      />,
      {
        title: `Edit profile picture`,
        isFullScreen: false,
        is80Percent: false,
        removePadding: true,
        onClose: () => resetStates(),
        useRadix: true,
      },
    )
  }

  useEffect(() => {
    if (isDone) {
      bg(async () => {
        await handleDone()
        setIsDone(false)
      })
    }
  }, [isDone])

  useEffect(() => {
    if (hasPicture) {
      bg(async () => {
        const res = await window.fetch(`/api/providers/${providerId}/avatar`, { credentials: `include` })
        const response = await res.json()
        if (response?.fileUrl) {
          setPreview(response.fileUrl)
        }
      })
    }
  }, [])

  return (
    <div className="flex items-start gap-3">
      <div className="group flex items-center justify-center">
        <label htmlFor={`picInput` + providerId} className="relative cursor-pointer" onClick={onAvatarClick}>
          <div
            className="absolute hidden h-full w-full items-center justify-center rounded-lg group-hover:flex"
            style={{ backgroundColor: `rgba(23,23,23,0.6)` }}
          >
            <EditPencil className="text-on-color" />
          </div>
          {!preview ? (
            <NameIcon fullName={getNameInitials(name)} heightClassName={`h-8`} widthClassName={`w-8`} isTiny />
          ) : (
            <img src={preview} height={32} width={32} className="rounded-lg object-cover" alt="" />
          )}
        </label>
      </div>
      <div className="flex flex-col items-start justify-center">
        <p className="text-heading-xs text-strong">{name}</p>
        <div className="flex items-center gap-2 text-body-xs text-subtle">
          <span>
            ID {remoteId.split(`_`).at(1) ?? remoteId}
            {pmsDisplayName && ` · ${pmsDisplayName}`}
          </span>
          {isSecondary && <span className="tag border-red-200 bg-red-100 text-red-700">Secondary</span>}
        </div>
      </div>
    </div>
  )
}
